import PropTypes from "prop-types"
import { isArray, isString, compact, chain, trim, get, values, omit } from 'lodash'

import { randstr, date2str, str2date } from '../../lib/Tools'
import overlap from './datesOverlaping'

/* Data structures */

export const emptyDay = {
  isOpen: true,
  times: [],
  allDayTimes: ""
}

const emptySchedule = {
  key: '',
  startDate: '',
  endDate: '',
  note: '',
  weekDays: [
    /* Domingo */ emptyDay,
    /* Lunes */ emptyDay,
    /* Martes */ emptyDay,
    /* Miercoles */ emptyDay,
    /* Jueves */ emptyDay,
    /* Viernes */ emptyDay,
    /* Sabado */ emptyDay,
  ]
}

const emptyException = {
  key: '',
  note: '',
  date: '',
  isOpen: false,
  times: [],
  allDayTimes: ""
}

export const emptyTimeTable = {
  allDay: false,
  schedules: {},
  exceptions: {}
}

/* Prop types */

export const schedulePropType = PropTypes.shape({
  key: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  note: PropTypes.string,
  weekDays: [
    PropTypes.object,
    PropTypes.object,
    PropTypes.object,
    PropTypes.object,
    PropTypes.object,
    PropTypes.object,
    PropTypes.object,
  ]
})

export const exceptionPropType = PropTypes.shape({
  key: PropTypes.string,
  date: PropTypes.string,
  note: PropTypes.string,
  times: PropTypes.arrayOf(PropTypes.string),
})

export const timetablePropType = PropTypes.shape({
  allDay: PropTypes.bool.isRequired,
  schedules: PropTypes.objectOf(schedulePropType).isRequired,
  exceptions: PropTypes.objectOf(exceptionPropType).isRequired
})

/* */

const arrayToStringHours = hours => hours.join(`\n`)
const arrayToStringRanges = ranges => ranges.map(range => `${range.start}-${range.end}`).join(`\n`)


/* Schedules */

export const addSchedule = (timetable, startDate, endDate) => {
  const key = randstr()
  const ntt = { ...timetable,
    schedules: {
      ...timetable.schedules,
      [key]: { ...emptySchedule, key, startDate, endDate }
    } }
  return ntt
}

// TODO no es mejor pasar directamente el objeto que hay en day??
export const parseDaySchedule = (timetable, schKey, day) => {
  const r = get(timetable, ['schedules', schKey, 'weekDays', day, 'times'])
  if (isArray(r)) return arrayToStringHours(r)
  else return r
}

export const parseAllDaySchedule = (timetable, schKey, day) => {
  const r = get(timetable, ['schedules', schKey, 'weekDays', day, 'allDayTimes'])
  if (isArray(r)) return arrayToStringRanges(r)
  else return r
}

export const makeSchedulesTimes = (timetable, schedule) => {
  let times = {}
  schedule.weekDays.forEach(
    (_day, i) => {
      times = {
        ...times,
        [i]: {
          isOpen: schedule.weekDays[i].isOpen,
          times: parseDaySchedule(timetable, schedule.key, i),
          allDayTimes: parseAllDaySchedule(timetable, schedule.key, i),
          regexp: true
        }
      }
    }
  ) 
  return times
}


export const deleteSchedule = (timetable, schKey) => ({
  ...timetable,
  schedules: omit(timetable.schedules, schKey)
})

export const editScheduleDates = (timetable, schKey, startDate, endDate) => ({
  ...timetable,
  schedules: {
    ...timetable.schedules,
    [schKey]: {
      ...timetable.schedules[schKey],
      startDate,
      endDate
    }
  }
})

export const editScheduleNote = (timetable, schKey, note) => ({
  ...timetable,
  schedules: {
    ...timetable.schedules,
    [schKey]: {
      ...timetable.schedules[schKey],
      note
    }
  }
})

export const editDaySchedule = (timetable, schKey, day, hours) => {
  const times = compact(
    isString(hours) ? hours.split(/\n/) : hours
  ).map(str => trim(str)).sort()

  const nwd = timetable.schedules[schKey].weekDays.map((xi, i) => (i == day ? { ...xi, times } : xi))

  const ntt = {
    ...timetable, 
    schedules: {
      ...timetable.schedules,
      [schKey]: {
        ...timetable.schedules[schKey],
        weekDays: nwd
      }
    }
  }

  return ntt
}

export const editAllDaySchedule = (timetable, schKey, day, hours) => {
  const allDayTimes = compact(
    isString(hours) ? hours.split(/\n/) : hours
  ).sort().map(range => {
    const rangeHours = trim(range).split("-")
    return { start: rangeHours[0], end: rangeHours[1] }
  })

  const nwd = timetable.schedules[schKey].weekDays.map((xi, i) => (i == day ? { ...xi, allDayTimes } : xi))

  const ntt = {
    ...timetable, 
    schedules: {
      ...timetable.schedules,
      [schKey]: {
        ...timetable.schedules[schKey],
        weekDays: nwd
      }
    }
  }

  return ntt
}

export const editDayOpen = (timetable, schKey, day, isOpen) => {
  const nwd = timetable.schedules[schKey].weekDays.map((xi, i) => (i == day ? { ...xi, isOpen } : xi))

  const ntt = {
    ...timetable, 
    schedules: {
      ...timetable.schedules,
      [schKey]: {
        ...timetable.schedules[schKey],
        weekDays: nwd
      }
    }
  }

  return ntt
}
/* Exceptions */

export const addException = (timetable, date) => ({
  ...timetable,
  exceptions: {
    ...timetable.exceptions,
    [date]: {
      ...emptyException,
      key: randstr(),
      date
    }
  }
})

export const deleteException = (timetable, date) => ({
  ...timetable,
  exceptions: omit(timetable.exceptions, date)
})

export const editExceptionNote = (timetable, date, note) => ({
  ...timetable,
  exceptions: {
    ...timetable.exceptions,
    [date]: {
      ...timetable.exceptions[date],
      note
    }
  }
})

export const editException = (timetable, date, hours) => {
  const times = hours.split(/\n/).map(s => trim(s)).filter(x => x).sort()
  return {
    ...timetable,
    exceptions: {
      ...timetable.exceptions,
      [date]: {
        ...timetable.exceptions[date],
        times
      }
    }
  }
}
export const editAllDayException = (timetable, date, hours) => {
  const times = hours.split(/\n/).sort().map(range => {
    const rangeHours = trim(range).split("-")
    if (rangeHours[0] && rangeHours[1]) return { start: rangeHours[0], end: rangeHours[1] }
    else return null
  })

  return {
    ...timetable,
    exceptions: {
      ...timetable.exceptions,
      [date]: {
        ...timetable.exceptions[date],
        allDayTimes: compact(times)
      }
    }
  }
}

export const editExceptionOpen = (timetable, date, value) => ({
  ...timetable,
  exceptions: {
    ...timetable.exceptions,
    [date]: {
      ...timetable.exceptions[date],
      isOpen: value
    }
  }
})

/* */

// #TODO these methods aren't covered by spec

export const deleteAny = (timetable, key) => (
  timetable.schedules[key]
    ? deleteSchedule(timetable, key)
    : deleteException(timetable, key)
)

export const parseException = (exception) => {
  if (isArray(exception.times)) return arrayToStringHours(exception.times)
  else return exception.times
}

export const parseAllDayException = (exception) => {
  if (isArray(exception.allDayTimes)) return arrayToStringRanges(exception.allDayTimes)
  else return exception.allDayTimes
}

export const checkOverlap = (timetable, startDate, endDate, omitScheduleKey = "") => {
  const dateRanges = values(timetable.schedules)
    .filter(sch => sch.key && sch.key != omitScheduleKey)
    .map(schedule => ({
      start: str2date(schedule.startDate),
      end: str2date(schedule.endDate)
    }))

  dateRanges.push({ start: str2date(startDate), end: str2date(endDate) })
  return overlap(dateRanges)
}

export const getLastScheduledDate = (timetable) => (
  chain(timetable.schedules).values().map(schedule => schedule.endDate).max().value()
)

/**
 *
 */

const _findSchedule = (schedulesArray, sdate) => (
  schedulesArray.find(sch => (sch.startDate <= sdate && sdate <= sch.endDate))
)

const _getWeekdayIndex = (sdate) => str2date(sdate).getDay()

const _findTimes = (definition, sdate) => (
  (!definition && [])
  || (definition.date && definition.times)
  || definition.weekDays[_getWeekdayIndex(sdate)].times
)
const _findAllDayTimes = (definition, sdate) => {
  if (definition) {
    if (definition.date) return definition.allDayTimes
    else return definition.weekDays[_getWeekdayIndex(sdate)].allDayTimes
  }
  else return ""
}
const _findOpen = (definition, definitionType, sdate) => {
  if (definitionType == "exception") return definition.isOpen
  else return definition.weekDays[_getWeekdayIndex(sdate)].isOpen
}

export const checkDate = (timetable, date) => {
  const sdate = isString(date) ? date : date2str(date)
  const schedulesArray = values(timetable.schedules)

  let definitionType = "undefined"
  let definition = null

  if (timetable.exceptions[sdate]) {
    definitionType = "exception"
    definition = timetable.exceptions[sdate]
  }
  else {
    const sch = _findSchedule(schedulesArray, sdate)
    if (sch) {
      definitionType = "schedule"
      definition = sch
    }
  }
  const times = _findTimes(definition, sdate)
  const allDayTimes = _findAllDayTimes(definition, sdate)
  const open = definition && _findOpen(definition, definitionType, sdate)
  const note = (definition && definition.note) || ""

  return { open, definitionType, definition, times, allDayTimes, note }
}
